import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Utils } from '../../shared/utils';
import {WebsocketService} from "../../shared/services/websocket.service";

export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const websocketService = inject(WebsocketService);
    const toastr = inject(ToastrService);
    const transloco = inject(TranslocoService);
    const router = inject(Router);
    const utils = inject(Utils);

    let newReq = req.clone();

    if (authService.accessToken) {
        newReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${authService.accessToken}`),
        });
    }

    return next(newReq).pipe(
        catchError((error) => handleError(error, utils, authService, websocketService, router, toastr, transloco, next, req)),
    );
};

const handleError = (
    error: HttpErrorResponse,
    utils: Utils,
    authService: AuthService,
    websocketService: WebsocketService,
    router: Router,
    toastr: ToastrService,
    transloco: TranslocoService,
    next: HttpHandlerFn,
    req: HttpRequest<unknown>,
): Observable<HttpEvent<unknown>> => {
    if (error instanceof HttpErrorResponse) {

        if (error.status === 401) {

            // Authentication errors
            if (error.error.type === AuthUtils.AUTHENTICATION) {
                console.log('Authentication error');
                toastr.error(transloco.translate('LOGIN_AGAIN'), transloco.translate('ALERTS.WRONG_EMAIL_OR_PASSWORD'));
                throw error;
            }

            // This is the error for the refresh token expiration
            if (error.error.type === AuthUtils.REFRESH_TOKEN_EXPIRED) {
                console.log('Refresh token expired');
                localStorage.removeItem(utils.REFRESH_TOKEN);
                toastr.error(transloco.translate('LOGIN_AGAIN'), transloco.translate('INVALID_REFRESH_TOKEN'));

                router.navigate(['sign-out']);

                throw error;
            }

            if (authService.getRememberMe()) {

                // Disconnect from web socket
                websocketService.disconnect(false);

                return refreshAccessToken(authService).pipe(
                    switchMap(() => {
                        const newReq = req.clone({
                            headers: req.headers.set('Authorization', `Bearer ${authService.accessToken}`),
                        });

                        // connect to the web socket if it is not yet
                        websocketService.connect(authService.accessToken);

                        return next(newReq);
                    }),
                    catchError(err => {
                        if (err.status == 403) {
                            console.log('Forbidden error from refresh token');
                            toastr.error(transloco.translate('REDIRECTED'), transloco.translate('FORBIDDEN'));
                        } else {
                            toastr.error('Unable to refresh session. Please log in again.', 'Error');
                        }
                        throw err;
                    }),
                );

            } else {
                if (error.error.type === AuthUtils.TOKEN_EXPIRED) {
                    console.log('Token expired');
                    toastr.error(transloco.translate('LOGIN_AGAIN'), transloco.translate('INVALID_TOKEN'));

                    router.navigate(['sign-out']);

                    throw error;
                }
            }


        } else if (error.status === 403) {
            console.log('Forbidden error');
            if (error.error.type === AuthUtils.NO_TOKEN_PROVIDED) {
                toastr.error(transloco.translate('LOGIN_AGAIN', transloco.translate('INVALID_TOKEN')));
                console.log('No token provided');
                router.navigate(['sign-out']);

            } else if (error.error.type === AuthUtils.ELIGIBILITY) {
                toastr.error(transloco.translate('REDIRECTED'), transloco.translate('FORBIDDEN'));
                console.log('Forbidden access - Interceptor - eligibility');
                // we should redirect user to some page with error message that he does not have permission to access the page
                // router.navigate(['/error/404']);
            }
            throw error;
        } else if (error.status === 400) {
            console.log(error);
            toastr.warning('', error.error.message);
            throw error;
        } else if (error.status === 429) {
            const langCode = transloco.getActiveLang();
            toastr.warning('', error.error[langCode]);
            throw error;
        } else if (error.status === 404) {
            console.log(error);
            toastr.warning('', transloco.translate('NOT_FOUND'));
            throw error;
        } else {
            toastr.error('', transloco.translate('SOMETHING_WENT_WRONG'));
            throw error;
        }
    }
};

const refreshAccessToken = (authService: AuthService): Observable<any> => {
    console.log('Refreshing token');
    return authService.refresh_token().pipe(
        switchMap((tokenResponse) => {
            authService.accessToken = tokenResponse.body.token;
            return [tokenResponse]; // Emit the token to indicate success
        }),
        catchError((error) => {
            authService.signOut();
            return throwError(error);
        }),
    );
};
